import React, { useState } from 'react';
import { Upload, Download, FileText, X, RefreshCw } from 'lucide-react';

function parseCSV(text) {
  const lines = [];
  let row = [];
  let inQuotes = false;
  let current = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(current.trim());
      current = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') i++;
      row.push(current.trim());
      if (row.some(cell => cell !== '')) lines.push(row);
      row = [];
      current = '';
    } else {
      current += char;
    }
  }
  if (current !== '' || row.length > 0) {
    row.push(current.trim());
    if (row.some(cell => cell !== '')) lines.push(row);
  }

  if (lines.length === 0) return [];
  const headers = lines[0].map(h => h.replace(/^\uFEFF/, '').trim());
  const dataRows = lines.slice(1);

  return dataRows.map(r => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = r[idx] !== undefined ? r[idx] : '';
    });
    return obj;
  });
}

const TEMPLATES_MAP = {
  hubs: {
    label: 'Dépôts',
    filename: 'template_hubs.csv',
    content: `name*,street*,postal_code*,city*,country,latitude*,longitude*,geocoding_precision\n"Centre Logistique Lausanne-Renens","Route de Prilly 25","1004","Lausanne","Suisse","46.5362","6.5891","exact"\n"Hub Riviera Montreux","Avenue des Alpes 15","1820","Montreux","Suisse","46.4312","6.9108","exact"`,
    required: ['name*', 'street*', 'postal_code*', 'city*', 'latitude*', 'longitude*'],
    optional: ['country', 'geocoding_precision']
  },
  trucks: {
    label: 'Véhicules',
    filename: 'template_trucks.csv',
    content: `name*,plate*,valhalla_costing,fuel_type,fuel_consumption_per_km,required_license,height_m,width_m,length_m,weight_t,max_payload_weight_kg,max_payload_volume_m3,autonomy_km,cost_per_km,color,status\n"Renault D-ZE (Camion 1)","VD 490 265","van","electric","0.220","B","2.20","2.00","5.80","3.50","1200.0","14.00","300","0.95","#38bdf8","active"\n"Mercedes Sprinter 12T (Camion 2)","VD 6 719","truck","diesel","0.140","C","3.80","2.50","8.50","12.00","6000.0","35.00","500","1.60","#f59e0b","active"\n"Peugeot e-Expert (Camion 3)","VD 882 104","van","electric","0.180","B","2.00","1.90","5.00","2.80","800.0","10.00","250","0.75","#22c55e","active"\n"Volvo FL Electric (Camion 4)","VD 912 304","truck","electric","0.280","C","3.60","2.50","8.20","16.00","7500.0","38.00","350","1.85","#a855f7","active"`,
    required: ['name*', 'plate*'],
    optional: ['valhalla_costing', 'fuel_type', 'fuel_consumption_per_km', 'required_license', 'height_m', 'width_m', 'length_m', 'weight_t', 'max_payload_weight_kg', 'max_payload_volume_m3', 'autonomy_km', 'cost_per_km', 'color', 'status']
  },
  drivers: {
    label: 'Livreurs',
    filename: 'template_drivers.csv',
    content: `first_name*,last_name*,email*,phone,cost_per_hour,driving_license\n"Fabien","Jaulin","fabien.jaulin@loops.ch","+41 79 123 45 67","38.50","B"\n"Julie","Vraison","julie.vraison@loops.ch","+41 78 987 65 43","42.00","C"\n"Marc","Antoine","marc.antoine@loops.ch","+41 79 444 33 22","38.50","B"\n"Alexandre","Rossi","alexandre.rossi@loops.ch","+41 76 555 44 33","45.00","C"`,
    required: ['first_name*', 'last_name*', 'email*'],
    optional: ['phone', 'cost_per_hour', 'driving_license']
  },
  rounds: {
    label: 'Tournées',
    filename: 'template_rounds.csv',
    content: `name*,code*,color,default_driver,default_truck,default_start_hub,default_end_hub,default_start_time,default_end_time\n"Lausanne Centre & Ouchy","LAUS-CENTRE","#22c55e","Fabien Jaulin","VD 490 265","Centre Logistique Lausanne-Renens","Centre Logistique Lausanne-Renens","07:30","17:00"\n"Lausanne Nord & Chailly","LAUS-NORD","#38bdf8","Marc Antoine","VD 882 104","Centre Logistique Lausanne-Renens","Centre Logistique Lausanne-Renens","07:30","17:00"\n"Riviera Vevey-Montreux","RIVIERA","#f59e0b","Julie Vraison","VD 6 719","Hub Riviera Montreux","Hub Riviera Montreux","07:30","17:00"\n"La Côte Morges-Nyon","LA-COTE","#a855f7","Alexandre Rossi","VD 912 304","Centre Logistique Lausanne-Renens","Centre Logistique Lausanne-Renens","07:30","17:00"`,
    required: ['name*', 'code*'],
    optional: ['color', 'default_driver', 'default_truck', 'default_start_hub', 'default_end_hub', 'default_start_time', 'default_end_time']
  },
  clients: {
    label: 'Clients',
    filename: 'template_clients.csv',
    content: `code*,name*,street*,postal_code*,city*,latitude*,longitude*,phone,email,roadbook,driver_postit,default_service_time_mins,default_round\n"CLI-1001","Hôtel-Restaurant Le Raisin","Place de la Palud 19","1003","Lausanne","46.5218","6.6334","+41 21 312 11 00","contact@raisin-lausanne.ch","Livraison par la ruelle arrière. Sonner à la cuisine.","Attention aux contremarches glissantes.","15","LAUS-CENTRE"\n"CLI-1002","Beau-Rivage Palace","Chemin de Beau-Rivage 21","1006","Lausanne","46.5081","6.6302","+41 21 613 33 33","info@brp.ch","Déposer les paquets à la réception principale.","Demander Mme Mercier.","20","LAUS-CENTRE"\n"CLI-1003","ECOCAFE SA","Rue des Fossées 21","1110","Morges","46.5098","6.4982","+41 21 801 22 33","commandes@ecocafe.ch","Quai de déchargement latéral.","Code porte: 4412B","12","LA-COTE"\n"CLI-1004","Jack Dandy's","Rue du Lac 46","1400","Yverdon-les-Bains","46.7785","6.6411","+41 24 425 66 77","shop@jackdandys.ch","Boutique en zone piétonne. Accès autorisé avant 10h.","Livrer impérativement avant 10h00.","10","LAUS-NORD"`,
    required: ['code*', 'name*', 'street*', 'postal_code*', 'city*', 'latitude*', 'longitude*'],
    optional: ['phone', 'email', 'roadbook', 'driver_postit', 'default_service_time_mins', 'default_round']
  },
  stops: {
    label: 'Stops',
    filename: 'template_stops.csv',
    content: `stop_number,client_code,client_name*,street*,postal_code*,city*,latitude*,longitude*,type,status,weight_kg,volume_m3,revenue,service_time_mins,estimated_arrival,actual_arrival,real_departure,delivery_date,instructions,driver_notes,time_window_1,default_round,job_id,sequence_order,estimated_index,real_index,related_pickup_stop_id,related_delivery_stop_id\n"STOP-101","CLI-1001","Hôtel-Restaurant Le Raisin","Place de la Palud 19","1003","Lausanne","46.5218","6.6334","delivery","pending","140.0","0.80","180.00","15","08:15","","","${new Date().toISOString().slice(0, 10)}","Livraison réception cuisine","Sonner à la cuisine.","07:30-11:00","LAUS-CENTRE","","1","1","","",""\n"STOP-102","CLI-1002","Beau-Rivage Palace","Chemin de Beau-Rivage 21","1006","Lausanne","46.5081","6.6302","delivery","pending","280.0","1.50","420.00","20","09:05","","","${new Date().toISOString().slice(0, 10)}","Déposer à la réception principale","Accusé de réception requis.","08:00-14:00","LAUS-CENTRE","","2","2","","",""`,
    required: ['client_name*'],
    optional: [
      'stop_number', 'client_code', 'type', 'status', 'weight_kg', 'volume_m3', 'revenue',
      'service_time_mins', 'estimated_arrival', 'actual_arrival', 'real_departure',
      'delivery_date', 'instructions', 'driver_notes', 'time_window_1', 'time_windows',
      'default_round', 'job_id', 'sequence_order', 'estimated_index', 'real_index',
      'related_pickup_stop_id', 'related_delivery_stop_id',
      'street', 'postal_code', 'city', 'latitude', 'longitude'
    ]
  },
  missions: {
    label: 'Missions',
    filename: 'template_missions.csv',
    content: `display_name*,status,driver,truck,round,start_hub,end_hub,estimated_departure,estimated_arrival,real_departure,real_arrival,estimated_cost,real_cost,estimated_length_km,real_length_km,real_start_km,real_end_km,job_comments\n"Tournée Lausanne Centre","planned","Fabien Jaulin","VD 490 265","LAUS-CENTRE","Centre Logistique Lausanne-Renens","Centre Logistique Lausanne-Renens","07:30","16:30","","","150.00","","62.50","","12400.0","","Tournée urbaine prioritaire"\n"Tournée Riviera Express","planned","Julie Vraison","VD 6 719","RIVIERA","Hub Riviera Montreux","Hub Riviera Montreux","08:00","17:00","","","195.00","","85.00","","24500.0","","Livraisons hôtelières"`,
    required: ['display_name*'],
    optional: ['status', 'driver', 'truck', 'round', 'start_hub', 'end_hub', 'estimated_departure', 'estimated_arrival', 'real_departure', 'real_arrival', 'estimated_cost', 'real_cost', 'estimated_length_km', 'real_length_km', 'real_start_km', 'real_end_km', 'job_comments']
  }
};

export default function CsvImportModal({ isOpen, onClose, onReloadData, initialEntity = 'stops' }) {
  if (!isOpen) return null;

  const [selectedEntity, setSelectedEntity] = useState(initialEntity || 'stops');
  const [parsedRows, setParsedRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const entityInfo = TEMPLATES_MAP[selectedEntity] || TEMPLATES_MAP.stops;

  const handleDownloadTemplate = () => {
    const blob = new Blob([entityInfo.content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', entityInfo.filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const processFile = (file) => {
    if (!file) return;

    setFileName(file.name);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const rows = parseCSV(text);
        setParsedRows(rows);
      } catch (err) {
        console.error('Error parsing CSV file:', err);
        setImportResult({ type: 'error', message: 'Fichier CSV invalide ou corrompu' });
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
    }
  };

  const handleStartImport = async () => {
    if (!parsedRows || parsedRows.length === 0) return;
    setIsImporting(true);
    setImportResult(null);

    try {
      const res = await fetch('/api/import/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity: selectedEntity,
          rows: parsedRows
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        const dateNote = data.primaryDate ? ` (Date détectée : ${data.primaryDate})` : '';
        setImportResult({
          type: 'success',
          message: `✅ ${data.importedCount} enregistrements importés avec succès dans la table "${entityInfo.label}"${dateNote} !`
        });
        if (onReloadData) {
          await onReloadData(data.primaryDate);
        }
      } else {
        setImportResult({
          type: 'error',
          message: `❌ Erreur lors de l'importation : ${data.error || data.details || 'Échec de la requête'}`
        });
      }
    } catch (err) {
      console.error('Import error:', err);
      setImportResult({ type: 'error', message: `❌ Erreur réseau ou serveur : ${err.message}` });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }}>
      <div className="overlay-card" style={{ width: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '14px', background: '#0f172a', border: '1px solid #334155', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)' }}>
        
        {/* HEADER BAR */}
        <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Upload size={22} color="#38bdf8" />
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#ffffff' }}>
              Importateur de Données CSV
            </h2>
          </div>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* MODAL BODY CONTENT */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* ROW 1: TABLE DROPDOWN (LEFT) + DOWNLOAD TEMPLATE BUTTON (RIGHT) ON SAME LINE */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <select
              className="table-input"
              style={{ flex: 1, padding: '9px 12px', fontSize: '0.9rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', fontWeight: '700', borderRadius: '8px' }}
              value={selectedEntity}
              onChange={e => {
                setSelectedEntity(e.target.value);
                setParsedRows(null);
                setFileName('');
                setImportResult(null);
              }}
            >
              <option value="hubs">Dépôts</option>
              <option value="trucks">Véhicules</option>
              <option value="drivers">Livreurs</option>
              <option value="rounds">Tournées</option>
              <option value="clients">Clients</option>
              <option value="stops">Stops</option>
              <option value="missions">Missions</option>
            </select>

            <button
              type="button"
              onClick={handleDownloadTemplate}
              style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid #38bdf8', padding: '9px 14px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
              title="Télécharger le modèle CSV à remplir"
            >
              <Download size={15} />
              <span>Télécharger modèle à remplir</span>
            </button>
          </div>

          {/* FIELDS INFO CARD */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '0.78rem', color: '#cbd5e1', lineHeight: '1.4' }}>
              <div><b style={{ color: '#ef4444' }}>Obligatoires :</b> {entityInfo.required.join(', ')}</div>
              <div style={{ marginTop: '4px' }}><b style={{ color: '#94a3b8' }}>Optionnels :</b> {entityInfo.optional.join(', ')}</div>
            </div>
          </div>

          {/* FILE UPLOAD DROPZONE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                border: isDragging ? '2px dashed #38bdf8' : (parsedRows ? '2px dashed #22c55e' : '2px dashed #334155'),
                borderRadius: '10px',
                padding: '24px',
                textAlign: 'center',
                background: isDragging ? 'rgba(56, 189, 248, 0.12)' : (parsedRows ? 'rgba(34, 197, 94, 0.08)' : '#1e293b'),
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onClick={() => document.getElementById('csv-file-input').click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <FileText size={32} color={isDragging ? '#38bdf8' : (parsedRows ? '#22c55e' : '#94a3b8')} style={{ marginBottom: '8px' }} />

              {fileName ? (
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '800', color: '#ffffff' }}>{fileName}</div>
                  {parsedRows && (
                    <div style={{ fontSize: '0.78rem', color: '#22c55e', marginTop: '4px', fontWeight: '700' }}>
                      ✅ {parsedRows.length} lignes détectées et prêtes à l'importation
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '700', color: isDragging ? '#38bdf8' : '#cbd5e1' }}>
                    {isDragging ? 'Déposez votre fichier .CSV ici' : 'Glissez-déposez votre fichier .CSV ici, ou cliquez pour parcourir'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* NOTIFICATION RESULT BANNER */}
          {importResult && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: '700',
                background: importResult.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                color: importResult.type === 'success' ? '#22c55e' : '#ef4444',
                border: `1px solid ${importResult.type === 'success' ? '#22c55e' : '#ef4444'}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{importResult.message}</span>
            </div>
          )}

        </div>

        {/* FOOTER ACTIONS */}
        <div style={{ background: '#1e293b', borderTop: '1px solid #334155', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            type="button"
            className="btn-action"
            onClick={onClose}
            style={{ padding: '8px 16px', background: '#0f172a', color: '#94a3b8', border: '1px solid #334155' }}
          >
            Fermer
          </button>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              disabled={!parsedRows || isImporting}
              onClick={handleStartImport}
              className="btn-action"
              style={{
                padding: '8px 20px',
                background: parsedRows ? '#22c55e' : '#334155',
                color: '#ffffff',
                border: 'none',
                fontWeight: '800',
                fontSize: '0.88rem',
                cursor: (!parsedRows || isImporting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isImporting ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  <span>Importation en cours...</span>
                </>
              ) : (
                <>
                  <Upload size={16} />
                  <span>Importer ({parsedRows ? parsedRows.length : 0} lignes)</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
